import {Trans} from '@lingui/macro';
import _, {filter, keyBy} from 'lodash';
import React, {FC, ReactNode, useMemo} from 'react';
import styled, {css} from 'styled-components';

import {fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {ChannelsFormatter} from '../../../../srcCommon/formatters/channelsFormatter';
import {
  SignatureChannelDropdownInputItem,
  SignaturesChannelDropdownInheritableProps,
} from './signaturesChannelDropdown';
import {renderSignatureChannelsDropdownAllChannelsLabel} from './signaturesChannelDropdownHelpers';

/*
 * Constant.
 */

const maxItemsToRender = 4;

/*
 * Props.
 */

type SignaturesChannelDropdownValueProps = SignaturesChannelDropdownInheritableProps & {
  placeholder: ReactNode;
};

/*
 * Style.
 */

const StyledIconDiv = styled.div`
  display: inline-block;
  vertical-align: middle;
  height: 18px;
  margin-right: 5px;
  color: ${(p) => p.theme.greys.shade70};
`;

const StyledPlaceholderSpan = styled.span`
  color: ${(p) => p.theme.greys.shade60};
`;

const StyledMetaSpan = styled.span<{$noPaddingRight?: boolean}>`
  color: ${(p) => p.theme.greys.shade60};
  padding: 0 5px;
  ${(p) =>
    p.$noPaddingRight &&
    css`
      padding-right: 0;
    `}
`;

const StyledOtherCountSpan = styled.span`
  font-weight: ${fontWeights.semibold};
`;

/*
 * Component.
 */

export const SignaturesChannelDropdownValue: FC<SignaturesChannelDropdownValueProps> = (props) => {
  const {selectedItems} = props;
  const channelsMap = useMemo(() => keyBy(selectedItems, 'id'), [selectedItems]);

  if (selectedItems === null || (!props.inputItems && selectedItems.length === 0)) {
    return <StyledPlaceholderSpan>{props.placeholder}</StyledPlaceholderSpan>;
  }

  const renderSelectedItems = () => (
    <ChannelsFormatter
      channels={selectedItems}
      limit={maxItemsToRender}
      renderChannelName={(name, id) => (
        <>
          {props.renderItemIcon ? (
            <StyledIconDiv>{props.renderItemIcon(channelsMap[id])}</StyledIconDiv>
          ) : null}
          {name}
        </>
      )}
      renderOthersText={(_othersText, _othersName, othersCount) => (
        <StyledOtherCountSpan>{`+${othersCount}`}</StyledOtherCountSpan>
      )}
    />
  );

  return (
    <>
      {renderSelectedAllChannelItems(selectedItems.length > 0, props.inputItems)}
      {renderSelectedItems()}
    </>
  );
};

/*
 * Helpers.
 */

function renderSelectedAllChannelItems(
  hasSelectedItems: boolean,
  inputItems: ReadonlyArray<SignatureChannelDropdownInputItem>,
) {
  const selectedInputItems = filter(inputItems, (item) => item.isSelected);
  const hasSelectedAllInputs = selectedInputItems.length === inputItems.length;

  if (hasSelectedAllInputs) {
    return selectedInputItems.length === 1
      ? selectedInputItems[0].label
      : renderSignatureChannelsDropdownAllChannelsLabel();
  }

  return _(selectedInputItems)
    .map((item, index) => (
      <React.Fragment key={item.key}>
        {index !== 0 && (
          <StyledMetaSpan>
            <Trans id="or">or</Trans>
          </StyledMetaSpan>
        )}
        {item.label}
        {index === selectedInputItems.length - 1 && hasSelectedItems && (
          <StyledMetaSpan>
            <Trans id="or">or</Trans>
          </StyledMetaSpan>
        )}
      </React.Fragment>
    ))
    .compact()
    .value();
}
